import { createContext, useState } from 'react';
import {
    Reward, RewardsResponse, RewardResponse, LoyaltyClient, LoyaltyClientResponse,
    LoyaltyClientsResponse, LoyaltyHistory, LoyaltyConfiguration, LoyaltyConfigurationsResponse,
    WalletConfig, WalletConfigResponse, WalletModel, WalletModelsResponse, WalletProgram, WalletConfigChanges, WalletSaveResult,
    WalletMyDesign, WalletMyDesignsResponse, WalletMyDesignChanges, WalletMyDesignResult
} from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';


type LoyaltyContextProps = {
    // Recompensas
    rewards: Reward[];
    totalPage: number;
    loadRewards: (page: number) => Promise<void>;
    addReward: (reward: Reward) => Promise<string>;
    updateReward: (reward: Reward) => Promise<string>;
    deleteReward: (id: number) => Promise<string>;

    // Clientes
    clients: LoyaltyClient[];
    loadClients: () => Promise<string>;
    addClient: (client: LoyaltyClient) => Promise<string>;
    searchClient: (email: String) => Promise<LoyaltyClient>;
    resendCard: (card: string, email: string) => Promise<string>;
    deleteClient: (loyaltyMemberId: string) => Promise<string>;
    getClientHistory: (loyaltyMemberId: string) => Promise<{ history?: LoyaltyHistory; error?: string }>;

    // Configuración del programa
    configurations: LoyaltyConfiguration[];
    loadConfigurations: () => Promise<string>;
    saveConfiguration: (configuration: LoyaltyConfiguration) => Promise<{ error?: string; clientesActualizados?: number }>;

    // Tarjeta digital (Apple Wallet y Google Wallet)
    loadWalletConfig: () => Promise<{ configs?: WalletConfig[]; error?: string }>;
    loadWalletModels: (tipo: WalletProgram) => Promise<{ models?: WalletModel[]; error?: string }>;
    getWalletModelBanner: (codigo: string, plataforma?: 'apple' | 'google') => Promise<Blob | null>;
    getWalletImage: (tipo: WalletProgram, imagen: WalletImage) => Promise<Blob | null>;
    saveWalletConfig: (tipo: WalletProgram, changes: WalletConfigChanges) => Promise<{ result?: WalletSaveResult; error?: string }>;
    previewWalletApple: (tipo: WalletProgram, modelo?: string) => Promise<{ file?: Blob; error?: string }>;
    previewWalletGoogle: (tipo: WalletProgram, modelo?: string) => Promise<{ url?: string; error?: string }>;
    syncWalletGoogle: (tipo: WalletProgram) => Promise<string>;
    loadMyDesigns: (tipo: WalletProgram) => Promise<{ designs?: WalletMyDesign[]; max?: number; error?: string }>;
    saveMyDesign: (codigo: string | null, changes: WalletMyDesignChanges) => Promise<{ result?: WalletMyDesignResult; error?: string }>;
    deleteMyDesign: (codigo: string) => Promise<{ result?: WalletMyDesignResult; error?: string }>;
}

export type WalletImage = 'logo' | 'icono' | 'logo-google' | 'strip' | 'hero-google';

// Algunos errores llegan como texto plano y otros como { error } o { mensaje }
const errorMessage = (e: any) => {
    const data = e?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.error || data?.mensaje || "Ocurrió un error de comunicación, intente de nuevo.";
}


// Campos para los POST multipart de la tarjeta digital (se omiten los no definidos)
const toFormData = (fields: Record<string, any>) => {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (value instanceof File) form.append(key, value, value.name);
        else form.append(key, String(value));
    });
    return form;
}

// En la tarjeta digital los 500 se muestran con un mensaje genérico (el detalle queda en el log del API)
const walletErrorMessage = async (e: any) => {
    const status = e?.response?.status;
    if (!status || status >= 500) return "Ocurrió un error al procesar la tarjeta digital, intente de nuevo.";

    let data = e?.response?.data;
    // Las descargas usan responseType "blob", así que el error JSON también llega como Blob
    if (data instanceof Blob) {
        try { data = JSON.parse(await data.text()); } catch { data = null; }
    }
    return data?.error || data?.mensaje || "Ocurrió un error de comunicación, intente de nuevo.";
}


export const LoyaltyContext = createContext({} as LoyaltyContextProps);

export const LoyaltyProvider = ({ children }: any) => {

    const [rewards, setRewards] = useState<Reward[]>([]);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [clients, setClients] = useState<LoyaltyClient[]>([]);
    const [configurations, setConfigurations] = useState<LoyaltyConfiguration[]>([]);


    // -------------------- Recompensas --------------------

    const loadRewards = async (pageActual) => {

        const resp = await topApi.get<RewardsResponse>('/loyalty/rewards');
        if (resp.status == 200) {
            setRewards([...resp.data.rewards]);
            setTotalPage(1);
        }
    }

    const addReward = async (reward: Reward) => {
        try {
            const resp = await topApi.post<RewardResponse>('/loyalty/rewards', { reward });
            setRewards([...rewards, resp.data.reward]);
            return 'success'

        } catch (e) {
            return errorMessage(e);
        }
    }

    const updateReward = async (reward: Reward) => {
        try {
            const resp = await topApi.put<RewardResponse>(`/loyalty/rewards`, { reward });

            setRewards(rewards.map(prod => {
                return (prod.id === reward.id)
                    ? { ...prod, ...reward, ...(resp.data.reward && typeof resp.data.reward === 'object' ? resp.data.reward : {}) }
                    : prod;
            }));

            return 'success'

        } catch (e) {
            return errorMessage(e);
        }
    }

    const deleteReward = async (id: number) => {
        try {
            await topApi.delete<RewardResponse>(`/loyalty/rewards/${id}`);
            setRewards((prevLoyalty) =>
                prevLoyalty.filter((reward) => reward.id! !== id)
            );
            return 'success'
        } catch (e) {
            return errorMessage(e);
        }
    }


    // -------------------- Clientes --------------------

    const loadClients = async () => {
        try {
            const resp = await topApi.get<LoyaltyClientsResponse>('/loyalty/clients');
            setClients(resp.data.clients ?? []);
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    const addClient = async (client: LoyaltyClient) => {
        try {
            await topApi.post<RewardResponse>('/loyalty/register-client', { client });
            return 'success'

        } catch (e) {
            return errorMessage(e);
        }
    }

    const searchClient = async (email: String) => {
        try {
            let client = {
                email: email
            }
            const resp = await topApi.post<LoyaltyClientResponse>('/loyalty/search-client', { client });
            return resp.data.client;

        } catch (e) {
            return null;
        }
    }

    const resendCard = async (card: string, email: string) => {
        try {
            let client = {
                loyaltyMemberId: card,
                email: email
            }

            await topApi.post<RewardResponse>('/loyalty/resendCard', { client });
            return 'success'

        } catch (e) {
            return errorMessage(e);
        }
    }

    const deleteClient = async (loyaltyMemberId: string) => {
        try {
            await topApi.post('/loyalty/clients/delete', { loyaltyMemberId });
            setClients(prev => prev.filter(client => client.loyaltyMemberId !== loyaltyMemberId));
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    const getClientHistory = async (loyaltyMemberId: string) => {
        try {
            const resp = await topApi.get<LoyaltyHistory>(`/loyalty/history/${encodeURIComponent(loyaltyMemberId)}`);
            return { history: resp.data };
        } catch (e) {
            return { error: errorMessage(e) };
        }
    }


    // -------------------- Configuración --------------------

    const loadConfigurations = async () => {
        try {
            const resp = await topApi.get<LoyaltyConfigurationsResponse>('/loyalty/configurations');
            setConfigurations(resp.data.configurations ?? []);
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    const saveConfiguration = async (configuration: LoyaltyConfiguration) => {
        try {
            const resp = await topApi.post('/loyalty/configurations', { configuration });
            // Al cambiar la meta de sellos, el API actualiza las tarjetas de los clientes
            return { clientesActualizados: Number(resp.data?.clientesActualizados ?? 0) };
        } catch (e) {
            return { error: errorMessage(e) };
        }
    }


    // -------------------- Tarjeta digital --------------------

    const loadWalletConfig = async () => {
        try {
            const resp = await topApi.get<WalletConfigResponse>('/loyalty/wallet/config');
            return { configs: resp.data.configuraciones ?? [] };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    const loadWalletModels = async (tipo: WalletProgram) => {
        try {
            const resp = await topApi.get<WalletModelsResponse>('/loyalty/wallet/modelos', { params: { tipoPrograma: tipo } });
            return { models: resp.data.modelos ?? [] };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    // Las imágenes requieren el token, por eso se descargan como Blob (no sirven en un <img src> directo)
    const getWalletModelBanner = async (codigo: string, plataforma: 'apple' | 'google' = 'apple') => {
        try {
            const banner = plataforma === 'google' ? 'banner-google' : 'banner';
            const resp = await topApi.get<Blob>(`/loyalty/wallet/modelos/${encodeURIComponent(codigo)}/${banner}`, { responseType: 'blob' });
            return resp.data;
        } catch (e) {
            return null;
        }
    }

    // null si el comercio aún no ha subido esa imagen (404)
    const getWalletImage = async (tipo: WalletProgram, imagen: WalletImage) => {
        try {
            const resp = await topApi.get<Blob>(`/loyalty/wallet/config/${tipo}/imagen/${imagen}`, { responseType: 'blob' });
            return resp.data;
        } catch (e) {
            return null;
        }
    }

    const saveWalletConfig = async (tipo: WalletProgram, changes: WalletConfigChanges) => {
        try {
            const form = toFormData(changes);
            const resp = await topApi.post<WalletSaveResult>(`/loyalty/wallet/config/${tipo}`, form);
            return { result: resp.data };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    const previewWalletApple = async (tipo: WalletProgram, modelo?: string) => {
        try {
            const resp = await topApi.post<Blob>(`/loyalty/wallet/config/${tipo}/probar`, null,
                { params: modelo ? { modelo } : undefined, responseType: 'blob' });
            return { file: resp.data };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    const previewWalletGoogle = async (tipo: WalletProgram, modelo?: string) => {
        try {
            const resp = await topApi.post<{ url: string }>(`/loyalty/wallet/config/${tipo}/probar-google`, null,
                { params: modelo ? { modelo } : undefined });
            return resp.data?.url ? { url: resp.data.url } : { error: 'No se recibió el enlace de Google Wallet.' };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    const syncWalletGoogle = async (tipo: WalletProgram) => {
        try {
            const resp = await topApi.post(`/loyalty/wallet/config/${tipo}/sincronizar-google`);
            if (resp.data?.sincronizado === false) return resp.data?.mensaje || 'No se pudo actualizar la tarjeta en Android.';
            return 'success';
        } catch (e) {
            return walletErrorMessage(e);
        }
    }


    // -------------------- Mis diseños --------------------

    const loadMyDesigns = async (tipo: WalletProgram) => {
        try {
            const resp = await topApi.get<WalletMyDesignsResponse>('/loyalty/wallet/mis-disenos', { params: { tipoPrograma: tipo } });
            return { designs: resp.data.disenos ?? [], max: resp.data.maximoPorTipo };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    // Sin código crea el diseño; con código lo edita
    const saveMyDesign = async (codigo: string | null, changes: WalletMyDesignChanges) => {
        try {
            const url = codigo ? `/loyalty/wallet/mis-disenos/${encodeURIComponent(codigo)}` : '/loyalty/wallet/mis-disenos';
            const resp = await topApi.post<WalletMyDesignResult>(url, toFormData(changes));
            return { result: resp.data };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }

    const deleteMyDesign = async (codigo: string) => {
        try {
            const resp = await topApi.post<WalletMyDesignResult>(`/loyalty/wallet/mis-disenos/${encodeURIComponent(codigo)}/eliminar`);
            return { result: resp.data };
        } catch (e) {
            return { error: await walletErrorMessage(e) };
        }
    }


    return (
        <LoyaltyContext.Provider value={{
            rewards, totalPage, loadRewards, addReward, updateReward, deleteReward,
            clients, loadClients, addClient, searchClient, resendCard, deleteClient, getClientHistory,
            configurations, loadConfigurations, saveConfiguration,
            loadWalletConfig, loadWalletModels, getWalletModelBanner, getWalletImage,
            saveWalletConfig, previewWalletApple, previewWalletGoogle, syncWalletGoogle,
            loadMyDesigns, saveMyDesign, deleteMyDesign
        }}>
            {children}
        </LoyaltyContext.Provider>
    );
}
